import { useState, useEffect } from "react";
import { Tabs, Typography, message, Tag } from "antd";
import { supabase } from "../lib/supabase";
import { trackEvent, trackFeatureUsage, captureError } from "../lib/posthog";
import { Component, PRICING_EVENTS } from "../types/pricing";
import PricingTemplatesTab from "../components/pricing/PricingTemplatesTab";
import ComponentsTab from "../components/pricing/ComponentsTab";
import CompanyPricingOverrides from "../components/pricing/CompanyPricingOverrides";
import LMDRatesTab from "../components/pricing/LMDRatesTab";
import LMDSettingsTab from "../components/pricing/LMDSettingsTab";
import OwnDeliveryTab from "../components/pricing/OwnDeliveryTab";
import AdditionalCostsTab from "../components/pricing/AdditionalCostsTab";
import CostOverviewOrderTab from "../components/pricing/CostOverviewOrderTab";
import { AdminNavBar } from "../components/AdminNavBar";

const { Title } = Typography;

// Record type for supabase component data
interface ComponentData {
  id: string;
  name: string;
  component_type: string;
  description?: string;
  calculation_type?: string;
  behavior_type?: string; // NEW: Added behavior_type
  applies_to_delivery_type?: "delivery" | "storage" | "both" | null;
  // Allow additional unknown properties
  [key: string]: string | number | boolean | null | undefined;
}

const InvoicingAdmin = () => {
  const [activeTab, setActiveTab] = useState("templates");
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);

  console.log("[DEBUG] InvoicingAdmin - Initializing component state");

  // Fetch pricing components from Supabase
  const fetchComponents = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("pricing_components")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      if (data) {
        // Map fields to match our Component interface
        const mappedComponents = data.map((item: ComponentData) => ({
          id: item.id,
          name: item.name,
          type: item.component_type,
          cost: 0, // Set default cost (will come from pricing_values)
          is_active: true, // Set default active state
          description: item.description,
          component_type: item.component_type,
          calculation_type: item.calculation_type,
          behavior_type: item.behavior_type || "base", // NEW: Include behavior_type with default
          applies_to_delivery_type: item.applies_to_delivery_type || null,
          applies_to_shipment_type: item.applies_to_shipment_type || null,
          applies_to_booking_type: item.applies_to_booking_type ?? "both",
          applies_to_unloading_location: item.applies_to_unloading_location ?? "both",
        }));
        setComponents(mappedComponents);
      }
    } catch (error) {
      console.error("Error fetching pricing components:", error);
      message.error("Failed to load pricing components");
      captureError(error, {
        source: "InvoicingAdmin",
        action: "fetchComponents",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Track page view
    trackEvent(PRICING_EVENTS.VIEW_PAGE);
    trackFeatureUsage("pricing_admin");

    fetchComponents();

    return () => {
      // Cleanup if needed
    };
  }, []);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    trackEvent("pricing_tab_changed", { tab: key });
  };

  // Try-catch wrapper around rendering to capture any React errors
  try {
    const tabItems = [
      {
        key: "templates",
        label: "Pricing Templates",
        children: <PricingTemplatesTab />,
      },
      {
        key: "components",
        label: "Components",
        children: (
          <ComponentsTab
            components={components}
            loading={loading}
            fetchComponents={fetchComponents}
          />
        ),
      },
      {
        key: "overrides",
        label: (
          <span>
            Company Pricing Overrides
            <Tag color="warning" style={{ marginLeft: 8 }}>
              WIP
            </Tag>
          </span>
        ),
        children: <CompanyPricingOverrides />,
      },
      {
        key: "lmd-rates",
        label: "LMD Rates",
        children: <LMDRatesTab />,
      },
      {
        key: "lmd-settings",
        label: "LMD Settings",
        children: <LMDSettingsTab />,
      },
      {
        key: "own-delivery",
        label: "Own Delivery",
        children: <OwnDeliveryTab />,
      },
      {
        key: "additional-costs",
        label: "Aanvullende Kosten",
        children: <AdditionalCostsTab />,
      },
      {
        key: "cost-overview-order",
        label: "Kostenoverzicht volgorde",
        children: <CostOverviewOrderTab />,
      },
    ];

    return (
      <div style={{ padding: 24 }}>
        <AdminNavBar />
        <Title level={2}>Invoicing Management</Title>

        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
        />
      </div>
    );
  } catch (error: unknown) {
    console.error("InvoicingAdmin - Render error:", error);
    return (
      <div
        style={{
          padding: "2rem",
          background: "#fff1f0",
          border: "1px solid #ffa39e",
        }}
      >
        <h2>Error Rendering Pricing Admin</h2>
        <p>An error occurred while rendering the Pricing Admin page:</p>
        <pre
          style={{ background: "#f5f5f5", padding: "1rem", overflow: "auto" }}
        >
          {error instanceof Error ? error.toString() : String(error)}
          {error instanceof Error ? error.stack : ""}
        </pre>
      </div>
    );
  }
};

export default InvoicingAdmin;
